import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import * as React from "react"
import { cn } from "src/common/utils"

const AnimatedDialogContext = React.createContext<{ open: boolean }>({ open: false })

function AnimatedDialog({ children, open: controlledOpen, defaultOpen, onOpenChange, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const handleOpenChange = React.useCallback((value: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(value)
    }
    onOpenChange?.(value)
  }, [isControlled, onOpenChange])

  return (
    <AnimatedDialogContext.Provider value={{ open }}>
      <DialogPrimitive.Root open={controlledOpen} defaultOpen={defaultOpen} onOpenChange={handleOpenChange} {...props}>
        {children}
      </DialogPrimitive.Root>
    </AnimatedDialogContext.Provider>
  )
}

const AnimatedDialogTrigger = DialogPrimitive.Trigger

const AnimatedDialogPortal = DialogPrimitive.Portal

const AnimatedDialogClose = DialogPrimitive.Close

const dialogVariants = {
  default: "bg-popover text-popover-foreground border-accent",
  medium: "bg-popover text-popover-foreground border-accent sm:max-w-md",
  wide: "bg-popover text-popover-foreground border-accent sm:max-w-[1200px]",
  large: "bg-popover text-popover-foreground border-accent sm:max-w-lg lg:max-w-xl",
  full: "bg-popover text-popover-foreground border-accent w-[calc(100vw-2rem)] sm:max-w-[85vw] max-h-[90vh] overflow-y-auto overflow-x-hidden",
}

type AnimatedDialogVariant = keyof typeof dialogVariants

interface AnimatedDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  variant?: AnimatedDialogVariant
  hideCloseButton?: boolean
  dismissOnOutsideClick?: boolean
}

const AnimatedDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} forceMount asChild {...props}>
    <motion.div
      className={cn("fixed inset-0 z-50 bg-black/80", className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    />
  </DialogPrimitive.Overlay>
))
AnimatedDialogOverlay.displayName = "AnimatedDialogOverlay"

const AnimatedDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  AnimatedDialogContentProps
>(({ className, variant = "default", hideCloseButton = false, dismissOnOutsideClick = true, children, onInteractOutside, ...props }, ref) => {
  const { open } = React.useContext(AnimatedDialogContext)

  return (
    <AnimatePresence>
      {open && (
        <AnimatedDialogPortal forceMount>
          <AnimatedDialogOverlay />
          <DialogPrimitive.Content
            ref={ref}
            forceMount
            asChild
            onInteractOutside={(e) => {
              if (!dismissOnOutsideClick) {
                e.preventDefault()
              }
              onInteractOutside?.(e)
            }}
            {...props}
          >
            <motion.div
              className={cn(
                "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg gap-4 border p-4 shadow-lg sm:rounded-md",
                dialogVariants[variant],
                className,
              )}
              initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
              animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
              exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 350,
              }}
            >
              {children}
              {!hideCloseButton && (
                <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )}
            </motion.div>
          </DialogPrimitive.Content>
        </AnimatedDialogPortal>
      )}
    </AnimatePresence>
  )
})
AnimatedDialogContent.displayName = "AnimatedDialogContent"

const AnimatedDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left max-w-full", className)} {...props} />
)
AnimatedDialogHeader.displayName = "AnimatedDialogHeader"

const AnimatedDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
AnimatedDialogFooter.displayName = "AnimatedDialogFooter"

const AnimatedDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-medium", className)}
    {...props}
  />
))
AnimatedDialogTitle.displayName = "AnimatedDialogTitle"

const AnimatedDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description asChild>
    <div ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  </DialogPrimitive.Description>
))
AnimatedDialogDescription.displayName = "AnimatedDialogDescription"

export {
  AnimatedDialog,
  AnimatedDialogClose,
  AnimatedDialogContent,
  AnimatedDialogDescription,
  AnimatedDialogFooter,
  AnimatedDialogHeader,
  AnimatedDialogOverlay,
  AnimatedDialogPortal,
  AnimatedDialogTitle,
  AnimatedDialogTrigger,
}
